import { useAuth } from '@/providers/auth'
import { usePermission } from '@/providers/permissions'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from 'react-query'
import { TriangleAlert } from 'lucide-react'
import { fetchSubscription } from '@/pages/Companies/GetCompany/SubscriptionSettings/api/fetchSubscription'

export function SubscriptionBanner() {
  const { hasPermission } = usePermission()
  const { company } = useAuth()
  const [dismissed, setDismissed] = useState(false)
  const [ready, setReady] = useState(false)
  const dismissalKey = company?.id ? `@Habitar:subscription-warning-dismissed:${company.id}` : null
  const canReadSubscription = hasPermission('subscriptions', 'read')
  const canManageBilling = hasPermission('subscriptions', 'write')

  const { data: subscription } = useQuery(['subscription', 'banner', company?.id], () => fetchSubscription(), {
    enabled: !!company?.id && canReadSubscription,
    staleTime: 1000 * 60 * 5,
    retry: false
  })

  useEffect(() => {
    setReady(false)
    const timer = setTimeout(() => setReady(true), 2000)
    return () => clearTimeout(timer)
  }, [company?.id])

  useEffect(() => {
    if (!dismissalKey) {
      setDismissed(false)
      return
    }
    let raw: string | null = null
    try {
      raw = window.localStorage.getItem(dismissalKey)
    } catch {
      raw = null
    }
    if (!raw) {
      setDismissed(false)
      return
    }
    const expiresAt = Number(raw)
    if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
      setDismissed(false)
      return
    }
    setDismissed(true)
  }, [dismissalKey])

  const data = useMemo(() => {
    if (!subscription?.currentPeriodEnd || dismissed) {
      return null
    }
    const targetDate = new Date(subscription.currentPeriodEnd)
    if (Number.isNaN(targetDate.getTime())) {
      return null
    }
    const diffMs = targetDate.getTime() - Date.now()
    const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24))
    const status = (subscription.status ?? '').toLowerCase()
    const inactiveStatuses = ['inactive', 'expired', 'canceled', 'cancelled', 'unpaid', 'incomplete_expired']
    const isInactive = inactiveStatuses.includes(status)
    const expired = isInactive && diffMs <= 0
    const expiringSoon = !isInactive && diffMs > 0 && days <= 3
    if (!expired && !expiringSoon) {
      return null
    }
    const formattedDate = targetDate.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    const message = expired
      ? `Seu plano expirou em ${formattedDate}.`
      : `Seu plano expira em ${days === 1 ? '1 dia' : `${days} dias`} (${formattedDate}).`
    const variant: 'destructive' | 'warning' = expired ? 'destructive' : 'warning'
    const billingPath = canManageBilling && company?.id ? `/empresas/${company.id}/configuracoes/assinaturas` : null
    return { message, variant, billingPath }
  }, [subscription?.currentPeriodEnd, subscription?.status, dismissed, canManageBilling, company?.id])

  if (!data || !ready) {
    return null
  }

  const handleDismiss = () => {
    if (!dismissalKey) return
    try {
      window.localStorage.setItem(dismissalKey, String(Date.now() + 24 * 60 * 60 * 1000))
    } catch {
      setDismissed(true)
      return
    }
    setDismissed(true)
  }

  return (
    <Alert variant={data.variant} role="status" className="mb-4">
      <TriangleAlert className="h-4 w-4" />
      <AlertDescription className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <span className="font-medium">{data.message}</span>
        <span className="flex items-center gap-1">
          {data.billingPath && (
            <Button asChild size="xs" variant="outline">
              <Link to={data.billingPath}>Atualizar plano</Link>
            </Button>
          )}
          <Button variant="ghost" size="xs" onClick={handleDismiss}>
            Dispensar
          </Button>
        </span>
      </AlertDescription>
    </Alert>
  )
}
